<template>
  <drawer @close="close()" width="680">
    <v-container class="px-5 px-md-18 py-14 py-md-8 pa-md-20">
      <v-card-title
        class="display-1 font-weight-bold pa-0 mb-8 letter-spacing-normal"
      >
        Checkout
      </v-card-title>
      <v-card-text class="pa-0">
        <v-row class="justify-lg-space-between align-center">
          <v-col sm="12">
            <template v-if="group && group.id">
              <v-row class="justify-space-between align-center mb-5 mb-mb-0">
                <v-col class="pa-0">
                  <v-card-title
                    class="py-0 title font-weight-bold letter-spacing-normal"
                  >
                    {{ getSummaryTitle(group.name) }}
                  </v-card-title>
                </v-col>
              </v-row>
              <v-row class="justify-lg-space-between align-center">
                <v-col class="pa-0">
                  <v-data-table
                    :headers="headers"
                    :items="group.species"
                    v-bind="
                      [].length > 5
                        ? { itemsPerPage: 5 }
                        : { hideDefaultFooter: true }
                    "
                    class="primary-table secondary-table border-radius-0 v-data-table-1 v-data-table-1--style3"
                  />
                </v-col>
              </v-row>
            </template>
            <v-row class="justify-lg-space-between align-center mt-10">
              <v-col class="pa-0">
                <v-card-title
                  class="py-0 title font-weight-bold letter-spacing-normal"
                >
                  Enter your payment information.
                </v-card-title>
              </v-col>
            </v-row>
            <v-card tile class="mt-5">
              <div
                class="justify-space-between align-items-center align-content-center d-flex py-4 mb-4 border-bottom--1  px-xl-12 px-lg-5"
              >
                <div class="text-left font-weight-bold">
                  Contribution Amount
                </div>
                <div class="text-right font-weight-bold">
                  USD $
                  <input ref="price" v-model="amount" type="number" />
                </div>
              </div>
            </v-card>
            <div class="stripe-payment">
              <StripeElements
                :stripe-key="stripeKey"
                :instance-options="instanceOptions"
                :elements-options="elementsOptions"
                #default="{ elements }"
                ref="elms"
              >
                <div class="cardElement">
                  <StripeElement
                    type="card"
                    :elements="elements"
                    :options="elementStyle"
                    ref="card"
                  />
                </div>
              </StripeElements>
              <span style="color: red" v-if="errors">
                {{ errors.message }}
              </span>
            </div>

            <v-card class="px-3 py-3 my-12">
              <v-list flat color="white">
                <v-list-item
                  :ripple="false"
                  inactive
                  class="justify-space-between px-xl-12 px-lg-5"
                >
                  <div class="text-left font-weight-bold mr-6">
                    Service Charge:({{ serviceCharge }}%)
                  </div>

                  <div class="text-right notranslate">
                    {{ chargeBreakDown.service }}
                  </div>
                </v-list-item>

                <v-list-item
                  :ripple="false"
                  inactive
                  class="justify-space-between px-xl-12 px-lg-5"
                >
                  <div class="text-left font-weight-bold mr-6">
                    Stripe Charge ({{ stripeCharge }}%)
                  </div>

                  <div class="text-right notranslate">
                    {{ chargeBreakDown.stripe }}
                  </div>
                </v-list-item>

                <v-list-item
                  :ripple="false"
                  inactive
                  class="justify-space-between px-xl-12 px-lg-5"
                >
                  <div class="text-left font-weight-bold mr-6">
                    Project
                  </div>

                  <div class="text-right notranslate">
                    {{ chargeBreakDown.project }}
                  </div>
                </v-list-item>
                <v-list-item
                  :ripple="false"
                  inactive
                  class="justify-space-between px-xl-12 px-lg-5"
                >
                  <div class="text-left font-weight-bold">Total</div>

                  <div class="text-right notranslate">
                    <template v-if="amount">
                      {{ formatter(this.amount) }}
                    </template>
                    <template v-else>
                      {{ this.chargeBreakDown.total }}
                    </template>
                  </div>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-container>
    <template v-slot:append>
      <div class="px-5 px-md-20 py-12">
        <v-btn block large class="primary" @click="pay()" :disabled="isLoading">
          Pay
        </v-btn>
      </div>
    </template>
  </drawer>
</template>

<script>
import { StripeElement, StripeElements } from 'vue-stripe-elements-plus';
import Request from '@request/CustomGroupRequest';
import Storage from '@services/Storage';
import { StoreNames } from '@constants/storage';
import SpeciesModel from '@models/Species';

const elementStyle = {};

const SERVICE_CHARGE = 17;
const STRIPE_CHARGE = 3;

const storage = new Storage();

export default {
  name: 'Checkout',
  components: {
    Drawer: () => import('@layouts/_partials/Drawer'),
    StripeElements,
    StripeElement,
  },
  props: {
    group: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    chargeBreakDown() {
      const amount = this.estimate.totalAmount || 0;
      const serviceCharge = (SERVICE_CHARGE / 100) * amount;
      const stripeCharge = (STRIPE_CHARGE / 100) * amount;
      const donationAmount = amount - stripeCharge - serviceCharge;

      return {
        service: this.formatter(serviceCharge),
        stripe: this.formatter(stripeCharge),
        project: this.formatter(donationAmount),
        total: this.formatter(amount),
        rawTotal: amount,
      };
    },
    amount: {
      get() {
        return this.estimate.totalAmount;
      },
      set(value) {
        this.estimate.totalAmount = Number(value);
      },
    },
  },
  data() {
    return {
      headers: [
        {
          text: 'Species',
          align: 'left',
          sortable: false,
          value: 'species.name',
        },
        { text: 'Count', value: 'count', width: 200 },
      ],
      elementStyle,
      serviceCharge: SERVICE_CHARGE,
      stripeCharge: STRIPE_CHARGE,
      stripeKey: process.env.VUE_APP_STRIPE_PUBLIC_KEY,
      instanceOptions: {},
      elementsOptions: {},
      errors: '',
      request: new Request(this.$refs),
      trips: [],
      isLoading: false,
      speciesModel: new SpeciesModel({
        itemsPerPage: 5,
      }),
      speciesList: [],
      estimate: {
        totalAmount: null,
      },
    };
  },
  methods: {
    formatter(amount) {
      const options = { style: 'currency', currency: 'USD' };

      return new Intl.NumberFormat('en-US', options).format(amount);
    },
    close(data) {
      this.$emit('exit', data);
    },
    async pay() {
      this.isLoading = true;
      this.errors = '';
      const groupComponent = this.$refs.elms;
      const card = this.$refs.card;
      const cardElement = card.stripeElement;
      await groupComponent.instance
        .createToken(cardElement)
        .then(async (result) => {
          if (result.error) {
            this.errors = result.error;
            this.isLoading = false;
            return;
          }
          const request = new Request(this.$refs, {
            payload: {
              stripe_token: result.token,
              data: this.trips,
              amount: this.estimate.totalAmount,
            },
          });
          let response = await request.submitBulk();
          if (response) {
            if (this.group) {
              await storage.delete(StoreNames.TRIPS, this.group.id);
            } else {
              await storage.truncate(StoreNames.TRIPS);
            }
            this.close({
              paymentSuccess: true,
            });
            this.isLoading = false;
          }
        });
    },
    async fetchAmount() {
      const request = new Request(this.$refs, {
        payload: {
          data: this.trips,
        },
      });
      let response = await request.getBulkCheckoutAmount();
      this.estimate = response.data;
    },
    getSummaryTitle(member) {
      let name = member;
      if (name.endsWith('s')) {
        return `${name}' Sightings`;
      }
      return `${name}'s Sightings`;
    },
  },
  async mounted() {
    this.trips =
      this.group && this.group.id
        ? [
            await storage.findFirst(StoreNames.TRIPS, {
              key: 'id',
              value: this.group.id,
            }),
          ]
        : await storage.find(StoreNames.TRIPS);
    await this.fetchAmount();

    this.$refs.price.focus();
  },
};
</script>

<style lang="scss" scoped>
.v-list-item::after {
  content: none;
}
.stripe-payment {
  .cardElement {
    height: 44px;
    border: 2px solid #c4c4c4;
    display: flex;
    align-items: center;
    padding: 0 12px;
    border-radius: 1px;
    > * {
      width: 100%;
    }
  }
  .row {
    display: flex;
    justify-content: space-between;
  }
  .cardNumber {
    width: 100%;
  }

  .cvc {
    max-width: 300px;
    min-width: 70px;
    width: 40%;
  }

  .expiryDate {
    max-width: 300px;
    min-width: 120px;
    width: 40%;
  }
}
</style>
